import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Authors_API, ImageUpload_API, ImageDelete_API, BASE_URL } from "./environment";
import { useAuth } from "../context/AuthContext";
import { FaEdit, FaTrash, FaPlus, FaFilter, FaSave, FaTimes } from "react-icons/fa";
import Swal from "sweetalert2";
import Pagination from "../pages/Pagination";
import "./JobModal.css"; // Reusing JobModal CSS

export default function Authors() {
  const { userData } = useAuth();
  const [authors, setAuthors] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [filters, setFilters] = useState({
    author_id: [],
    author_name: [],
    about_author: [],
    profile_pic: [],
  });
  const [showFilterPopup, setShowFilterPopup] = useState(null);
  const [tempSelected, setTempSelected] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [selectedAuthor, setSelectedAuthor] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("view");

  const columnDisplay = {
    author_id: "Author ID",
    author_name: "Author Name",
    about_author: "About Author",
    profile_pic: "Profile Picture",
  };

  const getDistinctValues = (column) => {
    const values = authors.map((item) => item[column]);
    const unique = [...new Set(values)];
    return unique.sort(
      (a, b) =>
        column === "author_id" ? a - b : a?.toString().localeCompare(b?.toString())
    );
  };

  const applyFilters = (item) => {
    return (
      (filters.author_id.length === 0 || filters.author_id.includes(item.author_id)) &&
      (filters.author_name.length === 0 || filters.author_name.includes(item.author_name)) &&
      (filters.about_author.length === 0 || filters.about_author.includes(item.about_author)) &&
      (filters.profile_pic.length === 0 || filters.profile_pic.includes(item.profile_pic))
    );
  };

  const filteredAuthors = authors.filter(applyFilters);
  const totalPages = Math.ceil(filteredAuthors.length / itemsPerPage);
  const paginatedAuthors = filteredAuthors.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const hasActiveFilters = Object.values(filters).some((arr) => arr.length > 0);

  useEffect(() => {
    const fetchAuthors = async () => {
      try {
        const response = await fetch(`${Authors_API}/getAll`, {
          headers: {
            Authorization: `Bearer ${userData.access_token}`,
            Accept: "application/json",
          },
        });
        if (!response.ok) throw new Error("Failed to fetch authors");
        const data = await response.json();
        setAuthors(data);
      } catch (error) {
        console.error("Error fetching authors:", error);
        Swal.fire("Error", "Failed to fetch authors.", "error");
      }
    };
    fetchAuthors();
  }, [userData.access_token]);

  const toggleRowSelection = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedRows.length === filteredAuthors.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(filteredAuthors.map((item) => item.author_id));
    }
  };

  const handleFilterClick = (column) => {
    setShowFilterPopup(column);
    setTempSelected(filters[column] || []);
    setSearchInput("");
  };

  const applyFilter = () => {
    const distinct = getDistinctValues(showFilterPopup);
    const newSelected = tempSelected.length === distinct.length ? [] : tempSelected;
    setFilters((prev) => ({ ...prev, [showFilterPopup]: newSelected }));
    setCurrentPage(1);
    setShowFilterPopup(null);
  };

  const uploadImage = async (file, authorName) => {
    if (!file) return null;
    const extension = file.name.split('.').pop();
    const renamedFile = new File([file], `author_${authorName}.${extension}`, { type: file.type });
    const formData = new FormData();
    formData.append("file", renamedFile);

    try {
      const response = await fetch(ImageUpload_API, {
        method: "POST",
        headers: { Authorization: `Bearer ${userData.access_token}` },
        body: formData,
      });
      if (!response.ok) throw new Error("Upload failed");
      const data = await response.json();
      return data.path;
    } catch (error) {
      Swal.fire("Error", "Failed to upload image.", "error");
      return null;
    }
  };

  const deleteImage = async (path) => {
    if (!path) return;
    try {
      await fetch(`${ImageDelete_API}?path=${encodeURIComponent(path)}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${userData.access_token}` },
      });
    } catch (error) {
      console.error("Error deleting image:", error);
    }
  };

  const addAuthor = () => {
    setSelectedAuthor({
      author_name: "",
      about_author: "",
      profile_pic: "",
      specialization: "",
      education: "",
      expertise: "",
      social_links: { linkedin: "", website: "", github: "", x: "" },
    });
    setModalMode("edit");
    setShowModal(true);
  };

  const viewAuthor = (author) => {
    setSelectedAuthor(author);
    setModalMode("view");
    setShowModal(true);
  };

  const handleSave = async (updatedAuthor) => {
    const payload = {
      author_name: updatedAuthor.author_name,
      about_author: updatedAuthor.about_author,
      profile_pic: updatedAuthor.profile_pic,
      specialization: updatedAuthor.specialization || null,
      education: updatedAuthor.education || null,
      expertise: updatedAuthor.expertise || null,
      social_links: updatedAuthor.social_links || null,
    };

    try {
      let response;
      if (updatedAuthor.author_id) {
        response = await fetch(`${Authors_API}/update/${updatedAuthor.author_id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userData.access_token}`,
          },
          body: JSON.stringify(payload),
        });
      } else {
        response = await fetch(`${Authors_API}/create`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userData.access_token}`,
          },
          body: JSON.stringify(payload),
        });
      }

      if (!response.ok) throw new Error("Failed to save author");

      const savedAuthor = await response.json();
      if (updatedAuthor.author_id) {
        setAuthors(authors.map((a) => (a.author_id === savedAuthor.author_id ? savedAuthor : a)));
      } else {
        setAuthors([...authors, savedAuthor]);
      }
      setShowModal(false);
      Swal.fire("Success!", "Author saved successfully.", "success");
    } catch (error) {
      Swal.fire("Error", "Failed to save author.", "error");
    }
  };

  const deleteAuthor = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await fetch(`${Authors_API}/get/${id}`, {
          headers: { Authorization: `Bearer ${userData.access_token}` },
        });
        if (!response.ok) throw new Error("Failed to fetch author");
        const author = await response.json();

        await fetch(`${Authors_API}/delete/${id}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${userData.access_token}` },
        });

        if (author.profile_pic) await deleteImage(author.profile_pic);

        setAuthors(authors.filter((a) => a.author_id !== id));
        setSelectedRows(selectedRows.filter((rowId) => rowId !== id));
        Swal.fire("Deleted!", "Author has been deleted.", "success");
      } catch (error) {
        Swal.fire("Error", "Failed to delete author.", "error");
      }
    }
  };

  const deleteSelectedRows = async () => {
    if (selectedRows.length === 0) return;

    Swal.fire({
      text: `Delete ${selectedRows.length} selected authors?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await Promise.all(
            selectedRows.map(async (id) => {
              const response = await fetch(`${Authors_API}/get/${id}`, {
                headers: { Authorization: `Bearer ${userData.access_token}` },
              });
              if (!response.ok) throw new Error("Fetch failed");
              const author = await response.json();

              await fetch(`${Authors_API}/delete/${id}`, {
                method: "DELETE",
                headers: { Authorization: `Bearer ${userData.access_token}` },
              });

              if (author.profile_pic) await deleteImage(author.profile_pic);
            })
          );

          setAuthors(authors.filter((a) => !selectedRows.includes(a.author_id)));
          setSelectedRows([]);
          Swal.fire("Success!", "Selected authors deleted.", "success");
        } catch (error) {
          Swal.fire("Error", "Failed to delete some authors.", "error");
        }
      }
    });
  };

  const renderFilterPopup = (column) => {
    const displayName = columnDisplay[column];
    const distinctValues = getDistinctValues(column);
    const filteredValues = distinctValues.filter(
      (val) =>
        val != null && val.toString().toLowerCase().includes(searchInput.toLowerCase())
    );
    const allVisibleSelected = filteredValues.every((val) => tempSelected.includes(val));

    const handleToggleVisible = (e) => {
      if (e.target.checked) {
        setTempSelected([...new Set([...tempSelected, ...filteredValues])]);
      } else {
        setTempSelected(tempSelected.filter((val) => !filteredValues.includes(val)));
      }
    };

    const clearSelections = () => {
      setFilters((prev) => ({ ...prev, [column]: [] }));
      setTempSelected([]);
      setShowFilterPopup(null);
    };

    return (
      <div className="filter-popup">
        <div className="filter-header">
          <span className="column-name">{displayName}</span>
          <span className="clear-filter" onClick={clearSelections}>Clear</span>
        </div>
        <div className="filter-search-container">
          <input type="checkbox" checked={allVisibleSelected} onChange={handleToggleVisible} />
          <input className="filter-search" type="text" placeholder="Search" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
          {searchInput && <span className="clear-search" onClick={() => setSearchInput("")}>×</span>}
        </div>
        <div className="suggestions-list">
          {filteredValues.length === 0 ? (
            <div>No results found</div>
          ) : (
            filteredValues.map((val) => (
              <label key={val?.toString() || "null"}>
                <input
                  type="checkbox"
                  checked={tempSelected.includes(val)}
                  onChange={(e) => {
                    setTempSelected((prev) =>
                      e.target.checked ? [...prev, val] : prev.filter((v) => v !== val)
                    );
                  }}
                />
                {val != null ? val.toString() : "N/A"}
              </label>
            ))
          )}
        </div>
        <div className="filter-buttons">
          <button onClick={() => setShowFilterPopup(null)}>Cancel</button>
          <button onClick={applyFilter}>Apply</button>
        </div>
      </div>
    );
  };

  return (
    <div className="relative">
      <Helmet>
        <title>Authors - SMTS Admin Panel</title>
      </Helmet>
      <div className="page-title">
        <h2>Authors</h2>
      </div>

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th className="hide-column">
                <input
                  type="checkbox"
                  checked={selectedRows.length === filteredAuthors.length && filteredAuthors.length > 0}
                  onChange={handleSelectAll}
                />
              </th>
              <th style={{ position: "relative" }}>
                {hasActiveFilters && (
                  <input
                    type="checkbox"
                    checked={selectedRows.length === filteredAuthors.length && filteredAuthors.length > 0}
                    onChange={handleSelectAll}
                  />
                )}
                Author ID
                <FaFilter
                  className={`filter-icon ${filters.author_id.length > 0 ? "active-filter" : ""}`}
                  onClick={() => handleFilterClick("author_id")}
                  style={{ marginLeft: "5px", cursor: "pointer" }}
                />
                {showFilterPopup === "author_id" && renderFilterPopup("author_id")}
              </th>
              <th style={{ position: "relative" }}>
                Author Name
                <FaFilter
                  className={`filter-icon ${filters.author_name.length > 0 ? "active-filter" : ""}`}
                  onClick={() => handleFilterClick("author_name")}
                  style={{ marginLeft: "5px", cursor: "pointer" }}
                />
                {showFilterPopup === "author_name" && renderFilterPopup("author_name")}
              </th>
              <th style={{ position: "relative" }}>
                About Author
                <FaFilter
                  className={`filter-icon ${filters.about_author.length > 0 ? "active-filter" : ""}`}
                  onClick={() => handleFilterClick("about_author")}
                  style={{ marginLeft: "5px", cursor: "pointer" }}
                />
                {showFilterPopup === "about_author" && renderFilterPopup("about_author")}
              </th>
              <th style={{ position: "relative" }}>
                Profile Picture
                <FaFilter
                  className={`filter-icon ${filters.profile_pic.length > 0 ? "active-filter" : ""}`}
                  onClick={() => handleFilterClick("profile_pic")}
                  style={{ marginLeft: "5px", cursor: "pointer" }}
                />
                {showFilterPopup === "profile_pic" && renderFilterPopup("profile_pic")}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedAuthors.map((author) => (
              <tr
                key={author.author_id}
                className={selectedRows.includes(author.author_id) ? "selected" : ""}
                onDoubleClick={() => toggleRowSelection(author.author_id)}
              >
                <td className="hide-column">
                  <input
                    type="checkbox"
                    checked={selectedRows.includes(author.author_id)}
                    onChange={() => toggleRowSelection(author.author_id)}
                  />
                </td>
                <td>{author.author_id}</td>
                <td>{author.author_name}</td>
                <td>{author.about_author}</td>
                <td>
                  {author.profile_pic ? (
                    <img
                      src={`${BASE_URL}${author.profile_pic}`}
                      alt="Profile"
                      style={{ width: "40px", height: "40px", objectFit: "cover" }}
                    />
                  ) : (
                    "N/A"
                  )}
                </td>
                <td className="actions">
                  <button
                    className={`action-button ${selectedRows.length > 1 ? "disabled-icon" : ""}`}
                    onClick={() => viewAuthor(author)}
                    disabled={selectedRows.length > 1}
                  >
                    <FaEdit />
                  </button>
                  <button
                    className={`action-button ${selectedRows.length > 1 ? "disabled-icon" : ""}`}
                    onClick={() => deleteAuthor(author.author_id)}
                    disabled={selectedRows.length > 1}
                  >
                    <FaTrash />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        setCurrentPage={setCurrentPage}
        setItemsPerPage={(value) => {
          setCurrentPage(1);
          setItemsPerPage(value);
        }}
      />

      {selectedRows.length > 0 && (
        <button className="delete-selected" onClick={deleteSelectedRows}>
          <FaTrash className="icon" /> ({selectedRows.length})
        </button>
      )}

      <button className="add-button" onClick={addAuthor}>
        <FaPlus />
      </button>

      {showModal && (
        <AuthorModal
          author={selectedAuthor}
          mode={modalMode}
          onClose={() => setShowModal(false)}
          onSave={handleSave}
          setMode={setModalMode}
          uploadImage={uploadImage}
          deleteImage={deleteImage}
          BASE_URL={BASE_URL}
        />
      )}
    </div>
  );
}

function AuthorModal({ author, mode, onClose, onSave, setMode, uploadImage, deleteImage, BASE_URL }) {
  const [editAuthor, setEditAuthor] = useState({
    ...author,
    social_links: author.social_links || { linkedin: "", website: "", github: "", x: "" },
  });
  const [newProfilePic, setNewProfilePic] = useState(null);

  const isEdit = mode === "edit";
  const isAdd = !author.author_id;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditAuthor({ ...editAuthor, [name]: value });
  };

  const handleSocialChange = (e) => {
    const { name, value } = e.target;
    setEditAuthor({
      ...editAuthor,
      social_links: { ...editAuthor.social_links, [name]: value },
    });
  };

  const handleImageChange = (e) => {
    if (e.target.files[0]) setNewProfilePic(e.target.files[0]);
  };

  const save = async () => {
    let finalProfilePic = editAuthor.profile_pic;

    if (newProfilePic) {
      if (editAuthor.profile_pic) await deleteImage(editAuthor.profile_pic);
      const uploadedPath = await uploadImage(newProfilePic, editAuthor.author_name);
      if (!uploadedPath) return;
      finalProfilePic = uploadedPath;
    }

    onSave({ ...editAuthor, profile_pic: finalProfilePic });
  };

  const startEdit = () => setMode("edit");

  const cancelEdit = () => {
    if (isAdd) {
      onClose();
    } else {
      setEditAuthor({
        ...author,
        social_links: author.social_links || { linkedin: "", website: "", github: "", x: "" },
      });
      setNewProfilePic(null);
      setMode("view");
    }
  };

  const renderField = (field, label, type = "text") => (
    <tr>
      <td>{label}</td>
      <td>
        {isEdit ? (
          type === "textarea" ? (
            <textarea name={field} value={editAuthor[field] || ""} onChange={handleChange} />
          ) : (
            <input type={type} name={field} value={editAuthor[field] || ""} onChange={handleChange} />
          )
        ) : (
          editAuthor[field] || "N/A"
        )}
      </td>
    </tr>
  );

  const renderSocialField = (key, label) => (
    <tr>
      <td>{label}</td>
      <td>
        {isEdit ? (
          <input
            type="url"
            name={key}
            value={editAuthor.social_links[key] || ""}
            onChange={handleSocialChange}
          />
        ) : editAuthor.social_links[key] ? (
          <a href={editAuthor.social_links[key]} target="_blank" rel="noopener noreferrer">
            {editAuthor.social_links[key]}
          </a>
        ) : (
          "N/A"
        )}
      </td>
    </tr>
  );

  return (
    <div className="job-modal-overlay">
      <div className="job-modal">
        <div className="job-modal-header">
          <h2>{isAdd ? "Add New Author" : `(${editAuthor.author_id}) ${editAuthor.author_name}`}</h2>
          <div className="job-modal-actions">
            {!isEdit && !isAdd && <button onClick={startEdit}><FaEdit /></button>}
            {isEdit && (
              <>
                <button onClick={save}><FaSave /></button>
                <button onClick={cancelEdit}><FaTimes /></button>
              </>
            )}
            <button className="job-modal-close" onClick={onClose}><FaTimes /></button>
          </div>
        </div>
        <table className="job-details-table">
          <tbody>
            {!isAdd && (
              <tr>
                <td>Author ID</td>
                <td>{editAuthor.author_id}</td>
              </tr>
            )}
            {renderField("author_name", "Author Name")}
            {renderField("about_author", "About Author", "textarea")}
            <tr>
              <td>Profile Picture</td>
              <td>
                {(editAuthor.profile_pic || newProfilePic) && (
                  <div style={{ marginBottom: "10px" }}>
                    {newProfilePic ? (
                      <img src={URL.createObjectURL(newProfilePic)} alt="New preview" style={{ width: "100px", height: "100px", objectFit: "cover" }} />
                    ) : (
                      <img src={`${BASE_URL}${editAuthor.profile_pic}`} alt="Current" style={{ width: "100px", height: "100px", objectFit: "cover" }} />
                    )}
                  </div>
                )}
                {isEdit && (
                  <input type="file" accept="image/*" onChange={handleImageChange} />
                )}
              </td>
            </tr>
            {renderField("specialization", "Specialization")}
            {renderField("education", "Education", "textarea")}
            {renderField("expertise", "Expertise", "textarea")}
            <tr><td colSpan="2"><strong>Social Links</strong></td></tr>
            {renderSocialField("linkedin", "LinkedIn")}
            {renderSocialField("website", "Website")}
            {renderSocialField("github", "GitHub")}
            {renderSocialField("x", "X (Twitter)")}
          </tbody>
        </table>
      </div>
    </div>
  );
}